import { identifierAndTypenameToId } from "@/objects/@utils";

import { FileChip } from "@/components/molecules/FileChip";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { EnumColors } from "@/constants/values";
import { Spinner } from "@/components/ui/spinner";
import { PresentedRow } from "./ToolCallCardReferences";
import type { ToolCallContent } from "./ToolCallCard.types";

/** The record a row of a tool call is working through, as the tool named it. */
type ItemNode = {
  identifier: string;
  typename: string;
  /** Its entity, for the box the title draws before the record is read. */
  entity?: { id: string; identifier: string };
};

function itemNode(item: ToolCallContent): ItemNode | null {
  const node = item.toolInput?.node as Partial<ItemNode> | undefined;
  return node?.identifier && node?.typename
    ? {
        identifier: node.identifier,
        typename: node.typename,
        entity: node.entity,
      }
    : null;
}

/**
 * Whether this row is one thing a tool is working through, rather than a tool
 * call of its own: the tool named the record it is on.
 */
export function isItemRow(item: ToolCallContent): boolean {
  return item.type === "tool_call" && itemNode(item) !== null;
}

/**
 * Whether the row is a file the user sent rather than a record: drawn as a
 * chip, so it sits inside the card's padding instead of running to its edges
 * the way a record's own layout does.
 */
export function isFileRow(item: ToolCallContent): boolean {
  return itemNode(item)?.typename === "MessageFileObject";
}

/**
 * One record a tool is working through, as the record itself: its row from the
 * moment the tool starts on it, turning while it works and saying how it went.
 */
export function ToolCallCardItemRow({ item }: { item: ToolCallContent }) {
  const node = itemNode(item);
  if (!node) return null;

  const isWorking = item.status !== "done";
  const created = item.toolOutput?.created_count;
  const outcome = item.toolError ? (
    <SimpleEnum label="Error" enumColor={EnumColors.ORANGE} size="sm" />
  ) : typeof created === "number" ? (
    <span className="text-[11px] font-semibold text-foreground">
      +{created}
    </span>
  ) : null;
  const label = String(item.toolInput?.label ?? "");

  // A file is still a file until it is a record: drawn as the one the user
  // sent, rather than as a record that does not exist yet.
  if (node.typename === "MessageFileObject") {
    return (
      <FileChip
        id={identifierAndTypenameToId(node.identifier, node.typename)}
        name={label}
        isWorking={isWorking}
        status={isWorking ? null : outcome}
      />
    );
  }

  return (
    <PresentedRow
      item={{
        identifier: node.identifier,
        __typename: node.typename,
        // The tool named it, so the row reads as the record before it is read.
        name: label,
        entity: node.entity,
      }}
      // Named with its entity, the row draws itself and reads the record only
      // when opened. Without one — a row from before tools said so — it reads
      // the record for the entity box rather than going without.
      loadOnOpen={Boolean(node.entity)}
      titleEndDecorator={
        isWorking ? <Spinner aria-label={`Working on ${label}`} /> : outcome
      }
    />
  );
}
